import { Plus, Settings2, Trash2 } from "@tamagui/lucide-icons";
import React from "react";

import type { ModalProps } from "react-native";
import { View, XStack, YStack } from "tamagui";
import { Body, BodyTextarea } from "../../../ui/Body";
import { Button } from "../../../ui/Button";
import { Checkbox } from "../../../ui/Checkbox";
import { Group } from "../../../ui/Group";
import { LabelTextarea } from "../../../ui/Label";
import { Modal } from "../../../ui/Modal";
import { PopoverConfirm } from "../../../ui/PopoverConfirm";
import { PopoverHelp } from "../../../ui/PopoverHelp";
import { Separator } from "../../../ui/Separator";
import { TipsText } from "../../../ui/TipsText";
import { Title } from "../../../ui/Title";
import type TrackerModuleModel from "../TrackerModuleModel";
import type { TrackerOption } from "../TrackerModuleModel";

export const TrackerOptionsModal: React.FC<
  {
    module: TrackerModuleModel;
    onClose: () => void;
  } & ModalProps
> = ({ module, visible, onClose }) => {
  const [options, setOptions] = React.useState(module.activeOptions);

  const saveOption = React.useCallback(
    async (o: TrackerOption) => {
      const optionIndex = options.findIndex((s) => s.uid === o.uid);
      if (optionIndex !== -1) {
        const oldValue = options[optionIndex];
        if (
          !!o.label &&
          o.weight &&
          (oldValue.isDeleted !== o.isDeleted ||
            oldValue.isDynamic !== o.isDynamic ||
            oldValue.label !== o.label ||
            oldValue.weight !== o.weight)
        ) {
          options[optionIndex] = o;
          setOptions([...options]);
          await module.saveOptions(options);
        }
      }
    },
    [options, module],
  );

  const dailyLimitOptions = [0, 1, 3, 5, 10];

  return (
    <Modal
      modalId="tracker_options"
      onClose={onClose}
      visible={visible}
      header={
        <YStack gap="$medium">
          <XStack gap="$medium" alignItems="center">
            <Settings2 />
            <Title>Options</Title>
          </XStack>
          <TipsText text={"Set a name, weight and edit your options here"} />
        </YStack>
      }
      footer={
        <YStack gap="$medium">
          <Button
            onPress={() => {
              setOptions([
                ...options,
                {
                  weight: 1,
                  label: "",
                  uid: `${Date.now()}`,
                  isDynamic: true,
                  isDeleted: false,
                },
              ]);
            }}
            icon={Plus}
          >
            Add option
          </Button>

          <Separator />

          <XStack gap="$medium" width={"100%"} alignItems="center">
            <Checkbox
              checked={module.data.settings.useBucketMode}
              onCheckedChange={(checked: boolean) => {
                module.saveSettings({ useBucketMode: checked });
              }}
            />
            <Body>Use variations mode?</Body>
            <PopoverHelp description="When enabled you need to pick all options once, before you can choose from every option again." />
          </XStack>

          <XStack gap="$medium" width={"100%"} alignItems="center">
            <XStack gap="$medium">
              <Body>Set a daily limit?</Body>
              <PopoverHelp description="When enabled you can only add up to this amount of entries" />
            </XStack>

            {dailyLimitOptions.map((limit) => {
              const label = limit === 0 ? "No" : limit;
              const selected =
                module.data.settings.useDailyLimit === limit ||
                (!module.data.settings.useDailyLimit && limit === 0);
              return (
                <Button
                  key={limit}
                  type={selected ? "primary" : "ghost"}
                  onPress={() => {
                    module.saveSettings({ useDailyLimit: limit });
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </XStack>

          <Button
            type="secondary"
            onPress={() => {
              onClose();
            }}
          >
            Close
          </Button>
        </YStack>
      }
    >
      <Group borderTop={false}>
        {options.length > 0 && (
          <XStack gap="$medium" width={"100%"} alignItems="center">
            <Body muted flex={1}>
              Name
            </Body>
            <XStack width={80} gap="$small">
              <Body muted>Weight</Body>
              <PopoverHelp description="Set a weight, setting 5 will for example multiply the amount with 5 when added. Setting 1 will produce amount * 1 when added as points." />
            </XStack>
            <XStack width={80} gap="$small">
              <Body muted>Multiple</Body>
              <PopoverHelp description="If checked, user can report amount as well, otherwise defaults to 1 * weight as points." />
            </XStack>
            <View width={40} />
          </XStack>
        )}

        {!options.length && (
          <View alignSelf="center" height={80} justifyContent="center">
            <Body muted textAlign="center">
              Add your first option below, then change the parameters to suit
              your needs
            </Body>
          </View>
        )}

        {options.map((o) => {
          if (o.isDeleted) {
            return null;
          }

          return (
            <XStack
              key={o.uid}
              gap="$medium"
              width={"100%"}
              alignItems="center"
              flexWrap="wrap"
            >
              <LabelTextarea
                maxHeight={50}
                placeholder="Set name..."
                submitOnEnter
                type="border"
                blurOnSubmit
                flex={1}
                onSubmitEditing={(e) =>
                  saveOption({ ...o, label: e.nativeEvent.text })
                }
                defaultValue={o.label}
              />

              <XStack width={80} gap="$small">
                <BodyTextarea
                  type="border"
                  placeholder="Set weight..."
                  submitOnEnter
                  textAlign="center"
                  blurOnSubmit
                  onSubmitEditing={(e) =>
                    saveOption({
                      ...o,
                      weight: Number.parseFloat(e.nativeEvent.text),
                    })
                  }
                  defaultValue={`${o.weight}`}
                />
              </XStack>

              <XStack width={80} gap="$small" justifyContent="center">
                <Checkbox
                  checked={o.isDynamic}
                  onCheckedChange={(checked: boolean) =>
                    saveOption({ ...o, isDynamic: checked })
                  }
                />
              </XStack>

              <View width={40}>
                <PopoverConfirm
                  onConfirm={() => {
                    saveOption({ ...o, isDeleted: true });
                  }}
                >
                  <Trash2 size={"$size.normal"} color={"$muted"} />
                </PopoverConfirm>
              </View>
            </XStack>
          );
        })}
      </Group>
    </Modal>
  );
};
